import React, { useRef, useState } from "react";
import { Bold, Italic, Heading2, Link2, List, Image as ImageIcon, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
  // Store absolute URL so it renders everywhere
  return `${BACKEND}${data.url}`;
}

export function MarkdownEditor({ value, onChange, testId = "md-editor" }) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const wrap = (before, after = before) => {
    const ta = ref.current;
    if (!ta) return;
    const s = ta.selectionStart, e = ta.selectionEnd;
    const sel = value.slice(s, e) || "text";
    const next = value.slice(0, s) + before + sel + after + value.slice(e);
    onChange(next);
    setTimeout(() => { ta.focus(); ta.selectionStart = s + before.length; ta.selectionEnd = s + before.length + sel.length; }, 0);
  };
  const prefixLine = (prefix) => {
    const ta = ref.current;
    if (!ta) return;
    const s = ta.selectionStart;
    const lineStart = value.lastIndexOf("\n", s - 1) + 1;
    const next = value.slice(0, lineStart) + prefix + value.slice(lineStart);
    onChange(next);
    setTimeout(() => ta.focus(), 0);
  };
  const insert = (text) => {
    const ta = ref.current;
    const s = ta ? ta.selectionStart : value.length;
    onChange(value.slice(0, s) + text + value.slice(s));
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      insert(`\n![image](${url})\n`);
    } catch (err) {
      alert("Image upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const btn = "w-9 h-9 rounded-lg flex items-center justify-center text-wanm-dark/70 hover:bg-wanm-offwhite hover:text-wanm-dark transition-colors";

  return (
    <div className="border-2 border-wanm-dark/15 rounded-xl overflow-hidden">
      <div className="flex items-center gap-1 p-2 border-b border-wanm-offwhite bg-wanm-offwhite/40">
        <button type="button" className={btn} onClick={() => prefixLine("## ")} title="Heading"><Heading2 size={18} /></button>
        <button type="button" className={btn} onClick={() => wrap("**")} title="Bold"><Bold size={18} /></button>
        <button type="button" className={btn} onClick={() => wrap("_")} title="Italic"><Italic size={18} /></button>
        <button type="button" className={btn} onClick={() => wrap("[", "](https://)")} title="Link"><Link2 size={18} /></button>
        <button type="button" className={btn} onClick={() => prefixLine("- ")} title="List"><List size={18} /></button>
        <button type="button" className={btn} onClick={() => fileRef.current?.click()} title="Insert image" data-testid={`${testId}-image`}>
          {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImageIcon size={18} />}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
        <span className="ml-auto text-xs text-wanm-dark/40 pr-2">Markdown</span>
      </div>
      <textarea
        ref={ref} value={value} onChange={(e) => onChange(e.target.value)} rows={12}
        data-testid={testId}
        className="w-full p-4 focus:outline-none resize-y font-mono text-sm leading-relaxed"
        placeholder="Write your article in Markdown…"
      />
    </div>
  );
}

export default MarkdownEditor;
